import { useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { confirmDialog } from 'primereact/confirmdialog';
import Cookies from 'universal-cookie';
import { Auth } from 'aws-amplify';
import { logout } from 'utils/utils';

const cookies = new Cookies();

export default function useAutoLogoutOnIdle(options = {}) {
  const { minutes = Number(process.env.REACT_APP_IDLE_LOGOUT_MINUTES || 120), websocketContext = null, history = null } = options;

  const hasLoggedOutRef = useRef(false);
  const storageKey = 'lpms:auto-logout';
  const warningTimeoutRef = useRef(null);

  const performLogout = async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    localStorage.setItem(storageKey, Date.now().toString());
    try {
      if (websocketContext && typeof websocketContext.closeConnection === 'function') {
        websocketContext.closeConnection();
      }
      logout(history);
    } catch (e) {
      console.log(e);
    }
  };

  // Cross-tab listener: when any tab triggers auto-logout, all tabs follow
  useEffect(() => {
    const onStorage = (e) => {
      if (e && e.key === storageKey && e.newValue) {
        performLogout();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []); // eslint-disable-line

  const onIdle = () => {
    // Only act when user is authenticated
    const token = cookies.get('token');
    if (!token) return;
    pause();
    Auth.signOut().catch((e) => {
      console.log(e);
    });

    // Show confirmation dialog before logging out
    confirmDialog({
      message: 'Due to inactivity, you have been automatically logged out. \nPlease sign in again to continue.',
      header: 'Session Timeout',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Okay',
      rejectLabel: '',
      acceptClassName: 'p-button-primary',
      rejectClassName: 'p-button-secondary d-none',
      closable: false,
      footerClassName: 'd-flex justify-content-center',
      style: { width: 400 },
      accept: () => {
        // User chose to logout
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
          warningTimeoutRef.current = null;
        }
        performLogout();
      },
      reject: () => null,
    });
  };

  const timeoutMs = Math.max(1, minutes) * 60 * 1000;

  const { pause } = useIdleTimer({
    onIdle,
    timeout: timeoutMs,
    throttle: 500,
    crossTab: true,
    stopOnIdle: false,
    startManually: false,
    syncTimers: true,
    events: ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'wheel', 'pointerdown', 'visibilitychange'],
  });
}
